import React, { useCallback, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from '@ublend-npm/aulaui-next';
import {
  Box,
  Button,
  Emoji,
  getEmojiByShortName,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { currentUserId } from '@core/selectors/user';
import { fetchingUser, users as usersSelector } from '@core/selectors/users';
import { fetchUsers } from '@core/actions/users';
import type { AulaState } from '@core/types/state';
import { selectors as reactionSelectors } from '@app/store/reactions';
import type { EmojiReaction } from './types';

const WAIT_MS = 150;

type User = {
  id?: string;
  objectId?: string;
  firstName?: string;
  lastName?: string;
};

type ReactionProps = {
  reaction: EmojiReaction;
  itemId: string;
  readOnly?: boolean;
  onToggle: (emojiName: string) => void;
};

const getUserId = (user: User) => user.id || user.objectId;

const getName = (user: User) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ');

export const formatReactionNames = ({
  users,
  userId,
  self,
}: {
  users: User[];
  userId: string | null;
  self: boolean;
}) => {
  const names = self
    ? [
        'You',
        ...users
          .filter((user) => getUserId(user) !== userId)
          .map(getName)
          .filter(Boolean),
      ]
    : users.map(getName).filter(Boolean);

  if (names.length < 2) return names[0] || '';
  if (names.length === 2) return names.join(' and ');

  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

const Reaction = ({
  reaction,
  itemId,
  readOnly = false,
  onToggle,
}: ReactionProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const userId = useSelector<AulaState, string | null>(currentUserId);
  const allUsers = useSelector<AulaState, { [userId: string]: User }>((state) =>
    usersSelector(state)
  );
  const userIds = useMemo(
    () => Object.keys(reaction.users || {}),
    [reaction.users]
  );
  const users = useMemo(
    () => userIds.map((id) => allUsers[id]).filter(Boolean),
    [allUsers, userIds]
  );
  const isMissingUsers = users.length !== userIds.length;
  const isFetchingUsers = useSelector<AulaState, boolean>(
    (state) =>
      userIds.length > 0 && userIds.every((id) => fetchingUser(state, id))
  );
  const isToggling = useSelector<AulaState, boolean>((state) =>
    reactionSelectors.isTogglingReaction(state, itemId, reaction.emojiName)
  );
  const shouldFetchUsers = isMissingUsers && !isFetchingUsers;
  const hasCurrentUserReacted = userId ? !!reaction.users?.[userId] : false;
  const isDisabled = readOnly || isToggling;
  const backgroundColor = hasCurrentUserReacted
    ? theme.palette.action.selected
    : theme.palette.background.paper;
  const borderColor = hasCurrentUserReacted
    ? theme.palette.primary.main
    : theme.palette.divider;

  const getUsers = useMemo(
    () =>
      debounce(() => {
        if (shouldFetchUsers) dispatch(fetchUsers(userIds));
      }, WAIT_MS),
    [dispatch, shouldFetchUsers, userIds]
  );

  useEffect(() => () => getUsers.cancel(), [getUsers]);

  const handleToggle = useCallback(() => {
    if (!isDisabled) onToggle(reaction.emojiName);
  }, [isDisabled, onToggle, reaction.emojiName]);

  const names = formatReactionNames({
    userId,
    users,
    self: hasCurrentUserReacted,
  });
  const tooltipTitle = (
    <Box sx={{ padding: '4px' }}>
      {!isMissingUsers ? (
        <Box>
          <Box component="span" sx={{ color: theme.palette.grey[500] }}>
            Reacted with
            {` :${reaction.emojiName}:`}
          </Box>
          <Box sx={{ marginTop: '2px' }}>{names}</Box>
        </Box>
      ) : (
        'Loading...'
      )}
    </Box>
  );

  const reactionButton = (
    <Button
      kind="secondary"
      aria-label={`${reaction.emojiName}, ${reaction.count} reaction${
        reaction.count === 1 ? '' : 's'
      }`}
      aria-pressed={hasCurrentUserReacted}
      aria-disabled={isDisabled}
      disabled={isToggling}
      onClick={handleToggle}
      onFocus={readOnly ? undefined : getUsers}
      onMouseEnter={readOnly ? undefined : getUsers}
      sx={{
        alignItems: 'center',
        appearance: 'none',
        backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '6px',
        color: theme.palette.text.primary,
        cursor: isDisabled ? 'default' : 'pointer',
        display: 'inline-flex',
        flexShrink: 0,
        fontFamily: theme.typography.fontFamily,
        gap: '4px',
        height: '32px',
        justifyContent: 'center',
        minWidth: '52px',
        width: '52px',
        opacity: isToggling ? theme.palette.action.disabledOpacity : 1,
        padding: '4px',
        '&:hover': {
          backgroundColor,
          borderColor: isDisabled ? borderColor : theme.palette.text.secondary,
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      <Emoji emoji={getEmojiByShortName(reaction.emojiName)} />
      <Box component="span" sx={{ fontSize: 14, lineHeight: '20px' }}>
        {reaction.count}
      </Box>
    </Button>
  );

  return isDisabled ? (
    reactionButton
  ) : (
    <Tooltip title={tooltipTitle}>{reactionButton}</Tooltip>
  );
};

export default Reaction;
